import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { brandGeometry } from "./brandGeometry";

export interface BrandProps {
  /**
   * `full`: the horizontal logo for the header. `compact`: the emblem alone, for narrow headers.
   * `entry`: the centred logo for the sign-in and other entry screens (spec 008 research R-07).
   */
  variant: "full" | "compact" | "entry";
  /** Overrides the default link; renders a button instead of an anchor. */
  onClick?: () => void;
  /** Link target. Defaults to the Today page. */
  to?: string;
}

export const BRAND_HOME_PATH = "/grading/today";

// Logo files and display heights; generated by packages/brand-kit into public/assets/brand.
const LOGOS = {
  full: { file: "L4", height: 44, aspect: brandGeometry.L4.aspect },
  compact: { file: "emblem", height: 36, aspect: brandGeometry.emblem.aspect },
  entry: { file: "L1", height: 96, aspect: brandGeometry.L1.aspect },
} as const;

export const Brand = ({
  variant,
  onClick,
  to = BRAND_HOME_PATH,
}: BrandProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const logo = LOGOS[variant];
  const surface = theme.palette.mode === "dark" ? "reverse" : "color";
  // The emblem alone carries no endorsement line, so its name omits it (FR-017).
  const brandName = t(
    variant === "compact" ? "brand.logoAltShort" : "brand.logoAlt",
  );
  const label = `${brandName}, ${t("nav.today")}`;
  const linkProps = onClick
    ? { component: "button" as const, type: "button" as const, onClick }
    : { component: RouterLink, to };

  return (
    <Box
      {...linkProps}
      aria-label={label}
      sx={(theme) => ({
        display: "inline-flex",
        alignItems: "center",
        flex: "none",
        minWidth: 0,
        p: 0,
        border: 0,
        background: "none",
        textDecoration: "none",
        cursor: "pointer",
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.focusRing}`,
          outlineOffset: 2,
        },
      })}
    >
      <img
        src={`/assets/brand/${logo.file}-${surface}.svg`}
        alt=""
        width={Math.round(logo.height * logo.aspect)}
        height={logo.height}
        draggable={false}
        decoding="async"
        fetchPriority={variant === "entry" ? "high" : "auto"}
        style={{ display: "block", maxWidth: "100%", height: "auto" }}
      />
    </Box>
  );
};

export default Brand;
